import { FitLine, FitZone } from './fitText';

/**
 * The double-bordered pad — the press block that frames the whole sheet in a
 * heavy rule with a hairline just inside it. The heading sits on lightly
 * patterned stock with the plate in one corner, the shop's name set large, the
 * proprietor in an outlined plate, and the address banded solid across the
 * sheet. Everything is printed in the one ink the shop chooses.
 *
 * Measured off a printed pad in A4 pixels and multiplied by `u` (the paper's
 * width against A4), so an A6 book is the same pad printed small.
 */

/** The heading block's height, as a share of the paper's width. */
const HEAD_RATIO = 0.295;

/** The words this pad prints that the other styles word differently. */
const BORDER_LABELS = {
    bn: { memoNo: 'ক্রমিক নং', inWords: 'কথায়', mobile: 'মোবাইল', colon: 'ঃ' },
    en: { memoNo: 'SL No.', inWords: 'In words', mobile: 'Mobile', colon: ':' },
};

/** The faint damask the heading is printed over. */
function HeadPattern({ ink }) {
    return (
        <svg
            width="100%"
            height="100%"
            aria-hidden="true"
            style={{ position: 'absolute', inset: 0, display: 'block' }}
        >
            <defs>
                <pattern id="bmDamask" width="54" height="54" patternUnits="userSpaceOnUse">
                    <circle cx="27" cy="27" r="11" fill="none" stroke={ink} strokeWidth="2" />
                    <circle cx="27" cy="27" r="3" fill={ink} />
                    <path d="M0 27 Q 13 13 27 27 Q 41 41 54 27" fill="none" stroke={ink} strokeWidth="1.6" />
                    <path d="M27 0 Q 41 13 27 27 Q 13 41 27 54" fill="none" stroke={ink} strokeWidth="1.6" />
                </pattern>
            </defs>
            <rect x="0" y="0" width="100%" height="100%" fill="url(#bmDamask)" opacity="0.1" />
        </svg>
    );
}

/** A solid tag and the bar written beside it — নাম, ঠিকানা. */
function TagBar({ label, u, ink }) {
    const height = 34 * u;
    return (
        <div style={{ display: 'flex', alignItems: 'stretch', height }}>
            <div
                style={{
                    flex: 'none', minWidth: 86 * u, background: ink, color: '#FFFFFF',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    padding: `0 ${10 * u}px`, fontSize: 19 * u, fontWeight: 700, lineHeight: 1.2,
                    whiteSpace: 'nowrap', boxSizing: 'border-box',
                }}
            >
                <span>{label}</span>
            </div>
            {/* The writing bar is printed as a soft tint, as the block is cut. */}
            <div
                style={{
                    flex: 1,
                    border: `${1.2 * u}px solid ${ink}`,
                    borderLeft: 'none',
                    background: 'linear-gradient(180deg, #FFFFFF 0%, #E9E9E9 55%, #FBFBFB 100%)',
                }}
            />
        </div>
    );
}

/** The ruled writing area: a plain head over an open grid, all in one ink. */
function BorderGrid({ memo, u, ink }) {
    const totalFlex = memo.columns.reduce((sum, col) => sum + (col.flex || 1), 0);
    const rule = `${1.2 * u}px solid ${ink}`;
    const last = memo.columns.length - 1;

    return (
        <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
            <colgroup>
                {memo.columns.map((col) => (
                    <col key={col.id} style={{ width: `${((col.flex || 1) / totalFlex) * 100}%` }} />
                ))}
            </colgroup>
            <thead>
                <tr>
                    {memo.columns.map((col, i) => (
                        <th
                            key={col.id}
                            style={{
                                color: ink,
                                height: 34 * u,
                                padding: `0 ${3 * u}px`,
                                fontSize: 19 * u,
                                fontWeight: 600,
                                lineHeight: 1.3,
                                textAlign: 'center',
                                borderTop: rule,
                                borderBottom: rule,
                                borderLeft: (memo.columnLines || i === 0) ? rule : 'none',
                                borderRight: (memo.columnLines || i === last) ? rule : 'none',
                            }}
                        >
                            <span>{col.label || ' '}</span>
                        </th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {Array.from({ length: memo.rows }, (_, i) => (
                    <tr key={i}>
                        {memo.columns.map((col, c) => (
                            <td
                                key={col.id}
                                style={{
                                    height: 30 * u,
                                    padding: 0,
                                    borderLeft: (memo.columnLines || c === 0) ? rule : 'none',
                                    borderRight: (memo.columnLines || c === last) ? rule : 'none',
                                    borderBottom: memo.rowLines && i < memo.rows - 1 ? rule : 'none',
                                }}
                            />
                        ))}
                    </tr>
                ))}
            </tbody>
        </table>
    );
}

/** মোট- / অগ্রিম- / বাকী-, ruled into the last two columns of the same grid. */
function BorderTotals({ memo, labels, u, ink }) {
    const rows = [
        memo.totals.total ? labels.total : null,
        memo.totals.advance ? labels.advance : null,
        memo.totals.due ? labels.due : null,
    ].filter(Boolean);

    const totalFlex = memo.columns.reduce((sum, col) => sum + (col.flex || 1), 0);
    const rule = `${1.2 * u}px solid ${ink}`;
    const span = Math.max(0, memo.columns.length - 2);

    if (!rows.length) {
        return <div style={{ borderTop: rule, borderLeft: rule, borderRight: rule, height: 0 }} />;
    }

    return (
        <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
            <colgroup>
                {memo.columns.map((col) => (
                    <col key={col.id} style={{ width: `${((col.flex || 1) / totalFlex) * 100}%` }} />
                ))}
            </colgroup>
            <tbody>
                {rows.map((label, i) => {
                    const lastRow = i === rows.length - 1;
                    return (
                        <tr key={label}>
                            {memo.columns.slice(0, span).map((col, c) => (
                                <td
                                    key={col.id}
                                    style={{
                                        borderLeft: (memo.columnLines || c === 0) ? rule : 'none',
                                        borderRight: memo.columnLines && c === span - 1 ? rule : 'none',
                                        borderBottom: lastRow ? rule : 'none',
                                        height: 32 * u,
                                        padding: 0,
                                    }}
                                />
                            ))}
                            <td
                                style={{
                                    border: rule,
                                    borderBottom: lastRow ? rule : 'none',
                                    height: 32 * u,
                                    padding: `0 ${8 * u}px`,
                                    fontSize: 19 * u,
                                    fontWeight: 700,
                                    lineHeight: 1.3,
                                    textAlign: 'right',
                                    color: ink,
                                }}
                            >
                                <span>{`${label}-`}</span>
                            </td>
                            <td style={{ border: rule, borderBottom: lastRow ? rule : 'none', height: 32 * u, padding: 0 }} />
                        </tr>
                    );
                })}
            </tbody>
        </table>
    );
}

/* ------------------------------------------------------------------ *
 *  The sheet
 * ------------------------------------------------------------------ */

export default function BorderMemo({ memo, labels, title, accent, paperWidth, u }) {
    const { shop, show } = memo;
    const lang = memo.docLang === 'en' ? 'en' : 'bn';
    const words = BORDER_LABELS[lang];
    const on = (key) => show[key] !== false;
    const ink = accent || '#121212';
    const pad = 16 * u;
    const headHeight = Math.round(paperWidth * HEAD_RATIO);
    const headPad = { top: 8 * u, side: 20 * u, bottom: 8 * u };
    const phones = [shop.phone, shop.phone2].filter((n) => String(n || '').trim());
    const email = String(shop.email || '').trim();
    const contactLine = [
        phones.length ? `${words.mobile}${words.colon} ${phones.join(', ')}` : '',
        on('email') ? email : '',
    ].filter(Boolean).join('   ');
    const addressLine = shop.address.split('\n').map((l) => l.trim()).filter(Boolean).join(' , ');
    const headKey = [
        memo.bismillah, title, shop.name, memo.proprietor, memo.distributor, shop.tagline,
        contactLine, shop.logo ? '1' : '0', JSON.stringify(show), u,
    ].join('~');

    return (
        // The pad's own frame: a heavy rule with a hairline just inside it.
        <div
            style={{
                flex: 1, display: 'flex', flexDirection: 'column',
                border: `${9 * u}px solid ${ink}`, boxSizing: 'border-box',
            }}
        >
            <div
                style={{
                    flex: 1, display: 'flex', flexDirection: 'column',
                    margin: 4 * u, border: `${1.5 * u}px solid ${ink}`, boxSizing: 'border-box',
                    overflow: 'hidden',
                }}
            >
                {/* ---- the heading, a fixed block on patterned stock ---- */}
                <div style={{ position: 'relative', flex: 'none', background: '#FBFBFB' }}>
                    {on('art') ? <HeadPattern ink={ink} /> : null}

                    {/* The plate sits in the corner of this block, and the logo
                        opposite it, so neither costs the heading any height. */}
                    {on('plate') ? (
                        <div
                            style={{
                                position: 'absolute', left: 14 * u, top: 12 * u, zIndex: 1,
                                background: '#FFFFFF', color: ink,
                                border: `${1.5 * u}px solid ${ink}`, borderRadius: 999 * u,
                                padding: `${2 * u}px ${14 * u}px`,
                                fontSize: 17 * u, fontWeight: 700, lineHeight: 1.35,
                            }}
                        >
                            <span>{title}</span>
                        </div>
                    ) : null}
                    {on('logo') && shop.logo ? (
                        <div
                            aria-hidden="true"
                            style={{
                                position: 'absolute', right: 14 * u, top: 12 * u, zIndex: 1,
                                width: 104 * u, height: 48 * u,
                                backgroundImage: `url("${shop.logo}")`,
                                backgroundRepeat: 'no-repeat',
                                backgroundPosition: 'right center',
                                backgroundSize: 'contain',
                            }}
                        />
                    ) : null}

                    <FitZone height={headHeight} align="centre" pad={headPad} signature={headKey}>
                        <div style={{ textAlign: 'center', color: ink }}>
                            {on('bismillah') && memo.bismillah.trim() ? (
                                <div style={{ fontSize: 15 * u, fontWeight: 500, lineHeight: 1.3 }}>
                                    {memo.bismillah}
                                </div>
                            ) : null}

                            <FitLine
                                text={shop.name || ' '}
                                size={60 * u}
                                style={{ marginTop: 2 * u, fontWeight: 800, lineHeight: 1.12 }}
                            />

                            {on('proprietor') && memo.proprietor.trim() ? (
                                <div style={{ display: 'flex', justifyContent: 'center', marginTop: 5 * u }}>
                                    <div
                                        style={{
                                            maxWidth: '72%', background: '#FFFFFF',
                                            border: `${1.5 * u}px solid ${ink}`, borderRadius: 3 * u,
                                            padding: `${2 * u}px ${18 * u}px`,
                                        }}
                                    >
                                        <FitLine
                                            text={memo.proprietor}
                                            size={20 * u}
                                            style={{ fontWeight: 700, lineHeight: 1.35, textAlign: 'center' }}
                                        />
                                    </div>
                                </div>
                            ) : null}

                            {on('phone') && contactLine ? (
                                <FitLine
                                    text={contactLine}
                                    size={21 * u}
                                    style={{ marginTop: 5 * u, fontWeight: 700, lineHeight: 1.4 }}
                                />
                            ) : null}

                            {on('distributor') && memo.distributor.trim() ? (
                                <FitLine
                                    text={memo.distributor}
                                    size={19 * u}
                                    style={{ marginTop: 3 * u, fontWeight: 700, lineHeight: 1.35 }}
                                />
                            ) : null}

                            {on('tagline') && shop.tagline.trim() ? (
                                <div
                                    style={{
                                        fontSize: 17 * u, fontWeight: 600, lineHeight: 1.4,
                                        margin: `${3 * u}px auto 0`, maxWidth: '88%',
                                    }}
                                >
                                    {shop.tagline}
                                </div>
                            ) : null}
                        </div>
                    </FitZone>
                </div>

                {/* ---- the address, banded solid across the sheet ---- */}
                {on('addressBand') && addressLine ? (
                    <div
                        style={{
                            flex: 'none', background: ink, color: '#FFFFFF', textAlign: 'center',
                            padding: `${4 * u}px ${20 * u}px`,
                        }}
                    >
                        <FitLine text={addressLine} size={21 * u} style={{ fontWeight: 700, lineHeight: 1.35 }} />
                    </div>
                ) : null}

                {/* ---- the form ---- */}
                <div
                    style={{
                        flex: 1, display: 'flex', flexDirection: 'column',
                        padding: `${12 * u}px ${pad}px ${16 * u}px`, boxSizing: 'border-box', color: ink,
                    }}
                >
                    {(memo.fields.memoNo || memo.fields.date) ? (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 * u, marginBottom: 8 * u }}>
                            <span style={{ fontSize: 19 * u, fontWeight: 700, lineHeight: 1.4 }}>
                                {memo.fields.memoNo ? `${words.memoNo}-` : ''}
                            </span>
                            {memo.fields.date ? (
                                <span style={{ display: 'flex', alignItems: 'stretch', height: 28 * u }}>
                                    <span
                                        style={{
                                            display: 'flex', alignItems: 'center', background: ink, color: '#FFFFFF',
                                            padding: `0 ${9 * u}px`, fontSize: 18 * u, fontWeight: 700, lineHeight: 1.2,
                                            whiteSpace: 'nowrap', boxSizing: 'border-box',
                                        }}
                                    >
                                        <span>{labels.date}</span>
                                    </span>
                                    {[40, 40, 62].map((width, i) => (
                                        <span
                                            key={i}
                                            style={{
                                                display: 'block', width: width * u, background: '#FFFFFF',
                                                border: `${1.2 * u}px solid ${ink}`, borderLeft: 'none', boxSizing: 'border-box',
                                            }}
                                        />
                                    ))}
                                </span>
                            ) : null}
                        </div>
                    ) : null}

                    {memo.fields.name ? <TagBar label={labels.name} u={u} ink={ink} /> : null}
                    {memo.fields.address ? (
                        <div style={{ marginTop: 6 * u }}>
                            <TagBar label={labels.address} u={u} ink={ink} />
                        </div>
                    ) : null}

                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', position: 'relative', marginTop: 8 * u }}>
                        <div style={{ flex: 1, minHeight: 0, display: 'flex', alignItems: 'stretch' }}>
                            <BorderGrid memo={memo} u={u} ink={ink} />
                        </div>
                        <BorderTotals memo={memo} labels={labels} u={u} ink={ink} />

                        {on('slogan') && memo.slogan.trim() ? (
                            <div
                                aria-hidden="true"
                                style={{
                                    position: 'absolute', left: '10%', right: '30%', bottom: '10%',
                                    textAlign: 'center', pointerEvents: 'none',
                                    fontSize: 28 * u, fontWeight: 600, lineHeight: 1.2,
                                    color: ink, opacity: 0.12, whiteSpace: 'nowrap',
                                    transform: 'skewX(-10deg)',
                                }}
                            >
                                {memo.slogan}
                            </div>
                        ) : null}
                    </div>

                    {memo.showWords ? (
                        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3 * u, marginTop: 12 * u }}>
                            <span style={{ fontSize: 19 * u, fontWeight: 700, lineHeight: 1.4, whiteSpace: 'nowrap' }}>
                                {`${words.inWords} ${words.colon}`}
                            </span>
                            <span style={{ flex: 1, borderBottom: `${2 * u}px dotted ${ink}`, height: 12 * u }} />
                        </div>
                    ) : null}

                    {memo.footerNote ? (
                        <div style={{ textAlign: 'center', marginTop: 8 * u, fontSize: 15 * u, fontWeight: 500, lineHeight: 1.4, opacity: 0.8 }}>
                            {memo.footerNote}
                        </div>
                    ) : null}

                    <div style={{ flex: 'none', minHeight: 30 * u }} />

                    {memo.showSignature ? (
                        <div
                            style={{
                                display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between',
                                gap: 24 * u, fontSize: 19 * u, fontWeight: 700, lineHeight: 1.35,
                            }}
                        >
                            <span>{labels.customerSign}</span>
                            <span>{labels.sellerSign}</span>
                        </div>
                    ) : null}
                </div>
            </div>
        </div>
    );
}
